/* eslint-disable max-lines -- the chat composer's one Composer wiring, carried whole from the app adapter. */
// The bound Work chat composer: the queued-message list, the active
// clarification card, the context-lock card and its extra sessions, the skill
// strip when the composer switches to Evidence, mode, stop and generating state,
// and the Composer's wiring. What the app owns crosses from the binding at
// `src/features/projects/components/ProjectChatComposer.tsx`: the session it
// runs, the desktop target, the clipboard paste, the object-refs flag, the image
// chips, the shortcuts flyout, and one module-level host of the app's components
// and composer ports.
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { cn } from '@heidi/utils';
import type { Editor } from '@tiptap/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { SlashSquare } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import {
  linkedContextItemKey,
  linkedContextItemLabel,
  linkedContextItemSubtitle,
  stripRefTags,
  type LinkedContextItem,
} from '../../../contracts';
import {
  artifactForLinkedItem,
  clarificationAnswerDraftAtom,
  composerLinkedItemsAtom,
  getQueuedTurnEditableBody,
  hasProjectsComposerSkill,
  isFoldableTextClarification,
  isWaitingForFoldedTextClarification,
  queuedTurnPreviewText,
  setOpenArtifactAtom,
  stripProjectsComposerSkills,
} from '../../../data';
import { Composer, type WorkComposerSessionAppearance } from '../../composer/Composer';
import { PhysicalContextCard } from '../../composer/PhysicalContextCard';
import { DesktopTargetSelector } from './DesktopTargetSelector';
import { QueuedMessageList, type QueuedMessageListItem } from './QueuedMessageList';
import type {
  WorkSessionComposerBinding,
  WorkSessionComposerProps,
} from './WorkSessionComposer.host';

const DEFAULT_SESSION_APPEARANCE: WorkComposerSessionAppearance = { kind: 'default' };

export const WorkSessionComposer = (
  props: WorkSessionComposerProps & WorkSessionComposerBinding
): JSX.Element => {
  const {
    isSubmitting = false,
    prefilledValue,
    voiceTrigger,
    className,
    toolbarLeading,
    speedMenu,
    composerMode,
    onComposerModeChange,
    sessionAppearance = DEFAULT_SESSION_APPEARANCE,
    sessionAutoFocus = true,
    onSessionFocusChange,
    expanded = false,
    onExpandedChange,
    composerDockLeading,
    composerBanner,
    sendDisabled = false,
    layout = 'full',
    toolbarSourcesSlot,
    disableFileDrop = false,
    evidenceSourcesSlot,
    patientScope,
    slashSectionOrder,
    sessionShortcutFlyout = false,
    chat,
    editorRef,
    desktopTarget,
    attachments,
    onClipboardPaste,
    chatObjectRefsEnabled,
    shortcutsFlyout,
    host,
    ...session
  } = props;
  const {
    ClarificationBlock,
    LinkedContextBadge,
    RegulatoryFootnote,
    VoiceTrigger,
    LinkContext,
    DictateSlot,
    DictationBar,
    PromptEditor,
  } = host;
  const intl = useIntl();
  const [tiptapEditor, setTiptapEditor] = useState<Editor | null>(null);
  const [isComposerPickerOpen, setIsComposerPickerOpen] = useState(false);
  const [areSuggestionsVisible, setAreSuggestionsVisible] = useState(false);
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const setLinkedItems = useSetAtom(composerLinkedItemsAtom);
  const clarificationDrafts = useAtomValue(clarificationAnswerDraftAtom);

  const isFocusedAppearance = sessionAppearance.kind === 'focused';
  const isLandingAppearance = isFocusedAppearance || sessionAppearance.kind === 'landing';
  const contextLocked = session.contextLock != null;
  const extraLinkedSessions = contextLocked
    ? session.linkedItems
        .slice(1)
        .filter(
          (item): item is Exclude<LinkedContextItem, { type: 'template' }> =>
            item.type !== 'template'
        )
    : [];
  const isDesktopTargetVisible =
    !isFocusedAppearance && !contextLocked && desktopTarget.visible;
  const isComposerLocked = session.runClosed || Boolean(session.readOnly);

  // An unmounting slot cannot report its own disappearance, so a mode flip while
  // the sheet is open would leave the card's bottom edge flat with nothing under it.
  const hasSuggestionsSlot =
    isFocusedAppearance && sessionAppearance.renderSuggestionsOverlay !== undefined;
  useEffect(() => {
    if (!hasSuggestionsSlot) setAreSuggestionsVisible(false);
  }, [hasSuggestionsSlot]);

  const offeredSkills = isFocusedAppearance ? [] : chat.availableSkills;
  const wasFocusedAppearanceRef = useRef(isFocusedAppearance);
  const latestDefaultDraftRef = useRef(chat.draft);
  if (!isFocusedAppearance) {
    latestDefaultDraftRef.current = chat.draft;
  }

  useEffect(() => {
    const wasFocused = wasFocusedAppearanceRef.current;
    wasFocusedAppearanceRef.current = isFocusedAppearance;
    if (wasFocused !== isFocusedAppearance) {
      queueMicrotask(() => editorRef.current?.focus('end'));
    }
    const previousDraft = latestDefaultDraftRef.current;
    if (wasFocused || !isFocusedAppearance || !hasProjectsComposerSkill(previousDraft)) {
      return;
    }

    const strippedDraft = stripProjectsComposerSkills(previousDraft);
    latestDefaultDraftRef.current = strippedDraft;
    editorRef.current?.replaceDraft(strippedDraft);
    toast({
      title: intl.formatMessage({
        defaultMessage: "Routines aren't available in Evidence",
        id: 'Y0zecDOHz+',
        description:
          'Toast after removing a routine chip when switching the landing composer to Evidence',
      }),
    });
  }, [chat.draft, editorRef, intl, isFocusedAppearance]);

  const queuedMessageItems = useMemo<QueuedMessageListItem[]>(
    () =>
      chat.queuedTurns.map((queuedTurn) => {
        const editableBody = getQueuedTurnEditableBody(queuedTurn.turn);
        return {
          id: queuedTurn.id,
          text: stripRefTags(queuedTurnPreviewText(queuedTurn.turn.previewText)),
          // Omit Edit when the turn cannot be faithfully rebuilt from a
          // plain Textarea (skills, workflows, object tokens, …).
          ...(editableBody !== null ? { editableText: editableBody } : {}),
        };
      }),
    [chat.queuedTurns]
  );

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center',
        isLandingAppearance && 'px-6 pb-6',
        className
      )}
    >
      <div
        className={cn(
          'mx-auto flex w-full min-w-0 max-w-full flex-col items-center @container/project-composer',
          isLandingAppearance
            ? cn(
                chat.queuedTurns.length > 0 ? 'gap-0' : 'gap-3',
                expanded ? 'max-w-5xl' : 'max-w-2xl'
              )
            : cn('max-w-3xl px-4 py-3', chat.queuedTurns.length > 0 ? 'gap-0' : 'gap-2')
        )}
      >
        {composerDockLeading}
        <QueuedMessageList
          items={queuedMessageItems}
          onSendNow={(id) => {
            const queuedTurn = chat.queuedTurns.find((turn) => turn.id === id);
            if (queuedTurn) chat.handleSendNow(queuedTurn);
          }}
          onRemove={chat.removeQueuedTurn}
          onClear={chat.clearQueuedTurns}
          onEdit={chat.editQueuedTurn}
          onEditingChange={chat.setQueuedEditing}
          testId="project-chat-queued-messages"
        />
        {session.activeClarificationApproval &&
        !isFoldableTextClarification(session.activeClarificationApproval.toolInput) &&
        !isWaitingForFoldedTextClarification(
          session.activeClarificationApproval.toolInput,
          clarificationDrafts[session.activeClarificationApproval.approvalId] ?? {}
        ) ? (
          <ClarificationBlock approval={session.activeClarificationApproval} />
        ) : null}
        <Composer
          host={{
            kind: 'project',
            lock: session.contextLock
              ? {
                  patientName: session.contextLock.label ?? undefined,
                  patientScoped: session.contextLock.kind === 'patient',
                }
              : null,
            analyticsSurface: session.analyticsSurface,
          }}
          layout={layout}
          className={cn(
            isLandingAppearance ? 'gap-3' : 'gap-2',
            // Pull the card up under the ghost queue header (parent gap alone
            // still reads large against the header's line box).
            chat.queuedTurns.length > 0 && '-mt-1'
          )}
          squareBottomEdge={areSuggestionsVisible}
          elevated={!isLandingAppearance}
          mode={
            onComposerModeChange
              ? (composerMode ?? 'work')
              : isFocusedAppearance
                ? 'evidence'
                : 'work'
          }
          onModeChange={onComposerModeChange}
          disableFileDrop={disableFileDrop}
          chatObjectRefsEnabled={chatObjectRefsEnabled}
          attachments={attachments}
          onSubmit={(payload) => chat.submitDraft(payload.draft)}
          onShortcutSubmit={(payload) => chat.handle.submitShortcut(payload.draft.text)}
          isSubmitting={isSubmitting}
          glow="while-generating"
          isGenerating={
            isSubmitting || session.turnInFlight || Boolean(session.isStartingSeededTurn)
          }
          disabled={isComposerLocked}
          placeholder={chat.placeholder}
          placeholderPreview={chat.placeholderPreview}
          rotatingPlaceholder={
            sessionAppearance.kind === 'default' && chat.isDefaultIdlePlaceholder
              ? sessionAppearance.rotatingPlaceholder
              : undefined
          }
          prefilledValue={prefilledValue}
          draftValue={chat.draft}
          onDraftChange={chat.setDraft}
          promptEditorRef={editorRef}
          availableSkills={chat.availableSkills}
          approvalMode={session.approvalMode}
          onApprovalModeChange={session.onApprovalModeChange}
          sendDisabled={sendDisabled}
          sendAriaLabel={chat.sendAriaLabel}
          sendSucceeded={chat.sendSucceeded}
          stopState={
            chat.showStop
              ? {
                  ariaLabel: chat.stopAriaLabel,
                  disabled: session.cancelling || isSubmitting || !session.turnInFlight,
                  pending: session.cancelling,
                  onStop: session.cancelTurn,
                }
              : undefined
          }
          voiceTrigger={isComposerLocked ? undefined : (voiceTrigger ?? <VoiceTrigger />)}
          promptOverflow={{
            expanded,
            onToggle: onExpandedChange ? () => onExpandedChange(!expanded) : undefined,
          }}
          bannerSlot={
            <>
              {composerBanner}
              {isDesktopTargetVisible ? (
                <div className="mb-2 flex w-full items-center ps-2">
                  <DesktopTargetSelector
                    desktopTarget={desktopTarget}
                    disabled={isComposerLocked}
                  />
                </div>
              ) : null}
              {session.contextLock ? (
                <PhysicalContextCard
                  lock={session.contextLock}
                  extras={
                    extraLinkedSessions.length > 0
                      ? extraLinkedSessions.map((item) => (
                          <LinkedContextBadge
                            key={linkedContextItemKey(item)}
                            type={item.type}
                            name={linkedContextItemLabel(item)}
                            onRemove={() =>
                              setLinkedItems((current) =>
                                current.filter(
                                  (existing, index) =>
                                    index === 0 ||
                                    linkedContextItemKey(existing) !==
                                      linkedContextItemKey(item)
                                )
                              )
                            }
                          />
                        ))
                      : undefined
                  }
                />
              ) : null}
            </>
          }
          cardTrailingSlot={
            isFocusedAppearance ? (
              // An absolute box resolves against the padding box, so without the
              // negative inset the sheet lands 2px inside the card's border.
              <div className="absolute -inset-x-px top-full">
                {sessionAppearance.renderSuggestionsOverlay?.({
                  editor: tiptapEditor,
                  isPickerOpen: isComposerPickerOpen,
                  onVisibilityChange: setAreSuggestionsVisible,
                })}
              </div>
            ) : null
          }
          toolbarLeadingSlot={toolbarLeading}
          toolbarSourcesSlot={
            composerMode === 'evidence' || isFocusedAppearance
              ? undefined
              : toolbarSourcesSlot
          }
          sourcesSlot={
            isFocusedAppearance ? (
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  data-testid="evidence-shortcuts-trigger"
                  aria-label={intl.formatMessage({
                    defaultMessage: 'View shortcuts',
                    id: 'jGtsrhU7ps',
                  })}
                  disabled={isComposerLocked || isSubmitting}
                  onClick={() => {
                    editorRef.current?.openShortcutPicker();
                  }}
                >
                  <SlashSquare size={16} />
                </Button>
                {layout !== 'condensed' ? (
                  <div className="shrink-0">{sessionAppearance.toolbarControl}</div>
                ) : null}
              </div>
            ) : composerMode === 'evidence' ? (
              evidenceSourcesSlot
            ) : undefined
          }
          linkedContextBadge={(item) => (
            <LinkedContextBadge
              // A template renders identically to a library document (same icon,
              // no subtitle), so map rather than widen the chip's union.
              type={item.type === 'template' ? 'library_document' : item.type}
              name={linkedContextItemLabel(item)}
              subtitle={linkedContextItemSubtitle(item)}
              onClick={() => {
                const artifact = artifactForLinkedItem(item);
                if (artifact) setOpenArtifact(artifact);
              }}
              onRemove={() => session.clearLinkedItems()}
            />
          )}
          renderAddMenu={LinkContext({
            onSet: chat.handleLinkContextSet,
            onAfterPick: chat.focusComposerAlways,
            skillsMenu:
              offeredSkills.length > 0
                ? {
                    skills: offeredSkills,
                    onInvoke: (skill) => {
                      editorRef.current?.insertSkill(skill);
                      editorRef.current?.focus('end');
                    },
                  }
                : undefined,
            speedMenu,
            hideLinkedContextPickers: contextLocked && !patientScope,
            hidePatientPicker: contextLocked,
            patientScope,
            disabled: isComposerLocked,
            renderShortcutsFlyout: sessionShortcutFlyout
              ? shortcutsFlyout.renderShortcutsFlyout
              : undefined,
          })}
          dictateSlot={DictateSlot({
            disabled: isComposerLocked,
          })}
          renderDictation={DictationBar}
          renderPromptEditor={PromptEditor({
            submitsEditorDraft: true,
            autoFocus: sessionAutoFocus && !isComposerLocked,
            disabled: isComposerLocked || chat.disableComposerForApprovalSteer,
            className: 'relative flex-1',
            editorClassName: cn(
              'overflow-y-auto bg-transparent text-text-primary',
              expanded ? 'h-[min(58dvh,44rem)]' : 'max-h-48'
            ),
            includeCreateWorkflow: !isFocusedAppearance && chat.isWorkflowsEnabled,
            includeRecordAction: session.recordingEligible,
            slashSectionOrder,
            onLinkContext: chat.handleLinkContextSet,
            onUnhandledKeyDown: chat.handleUnhandledEditorKeyDown,
            onFocus: () => {
              chat.handleEditorFocus();
              onSessionFocusChange?.(true);
            },
            onBlur: () => {
              chat.handleEditorBlur();
              onSessionFocusChange?.(false);
            },
            onClipboardPaste,
            onEditorChange: setTiptapEditor,
            onPickerOpenChange: setIsComposerPickerOpen,
            patientScope,
          })}
        />
        {/* Bound composer only. Always shown, regardless of whether the
            chat has called Evidence. */}
        {!isLandingAppearance ? (
          <div className="mt-2" data-testid="project-chat-evidence-regulatory-slot">
            <RegulatoryFootnote />
          </div>
        ) : null}
        {/* Evidence-mode Home dock only; the footnote resolves the IFU region from
            user + team state, so it mounts with the focused appearance rather than
            on every dock render. */}
        {isFocusedAppearance ? <RegulatoryFootnote className="mt-2" /> : null}
        {sessionShortcutFlyout ? shortcutsFlyout.marketplace : null}
      </div>
    </div>
  );
};
